import React from 'react';
import {
  alpha,
  Box,
  Chip,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  Add,
  Article,
  Delete,
  Edit,
  Label,
  PictureAsPdf,
  SettingsSuggest,
  Tune,
  UploadFile,
} from '@mui/icons-material';

const configModules = [
  {
    title: 'Nutritional parameters',
    description: 'Define units, codes and labelling values',
    active: true,
  },
  {
    title: 'Annotation labels',
    description: 'Configure allergens, additives and properties',
    active: false,
  },
  {
    title: 'Supporting documents',
    description: 'Upload and manage reference documents',
    active: false,
  },
];

const nutritionalParameters = [
  {
    name: 'Protein, total',
    code: 'PROT',
    unit: 'g',
    labelUnit: 'g',
  },
  {
    name: 'Energy, kcal',
    code: 'ENERC',
    unit: 'kcal',
    labelUnit: 'kcal',
  },
  {
    name: 'Sugars, total',
    code: 'SUGAR',
    unit: 'g',
    labelUnit: 'g',
  },
];

const annotationExamples = [
  'Milk and products thereof',
  'Nuts and products thereof',
  'Solubility',
];

const documentExamples = [
  'EU nutrition claims.pdf',
  'Labelling guidance.pdf',
];

function ActionButton({ type = 'edit' }) {
  const isDelete = type === 'delete';

  return (
    <Box
      sx={{
        width: 23,
        height: 23,
        borderRadius: '50%',
        bgcolor: (theme) =>
          isDelete
            ? alpha(theme.palette.error.main, 0.08)
            : alpha(theme.palette.primary.main, 0.08),
        color: isDelete ? 'error.main' : 'primary.main',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {isDelete ? <Delete sx={{ fontSize: 13 }} /> : <Edit sx={{ fontSize: 13 }} />}
    </Box>
  );
}

function CreateButton({ label, icon }) {
  return (
    <Box
      sx={{
        px: 1,
        py: 0.55,
        borderRadius: 1.5,
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
        color: 'primary.main',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.55,
        fontSize: 10.5,
        fontWeight: 900,
        whiteSpace: 'nowrap',
      }}
    >
      {icon}
      {label}
    </Box>
  );
}

export default function ManagementDocsConfigurationVisual() {
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 285,
        p: 1.6,
        bgcolor: 'background.paper',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          width: '100%',
          borderRadius: 2.5,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          boxShadow: '0 14px 28px rgba(15, 23, 42, 0.05)',
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 1.7,
            py: 1.2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SettingsSuggest sx={{ fontSize: 16 }} />
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                Configuration center
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.2 }}
              >
                Custom reference data for labelling and formulation workflows
              </Typography>
            </Box>
          </Stack>

          <Chip
            label="Custom setup"
            size="small"
            sx={{
              height: 23,
              fontSize: 10.5,
              fontWeight: 800,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              color: 'primary.main',
            }}
          />
        </Stack>

        {/* Body */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '0.9fr 1.55fr' },
            minHeight: 220,
          }}
        >
          {/* Left: configuration modules */}
          <Box
            sx={{
              p: 1.6,
              borderRight: { sm: '1px solid rgba(15, 23, 42, 0.08)' },
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.016),
            }}
          >
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mb: 1.1,
                fontWeight: 800,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: 0.4,
              }}
            >
              Admin modules
            </Typography>

            <Stack spacing={0.9}>
              {configModules.map((module) => (
                <Paper
                  key={module.title}
                  variant="outlined"
                  sx={{
                    p: 1.15,
                    borderRadius: 2,
                    bgcolor: module.active
                      ? (theme) => alpha(theme.palette.primary.main, 0.06)
                      : 'background.paper',
                    borderColor: module.active
                      ? (theme) => alpha(theme.palette.primary.main, 0.25)
                      : 'divider',
                  }}
                >
                  <Stack direction="row" spacing={0.85} alignItems="flex-start">
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        bgcolor: module.active
                          ? (theme) => alpha(theme.palette.primary.main, 0.12)
                          : (theme) => alpha(theme.palette.grey[500], 0.08),
                        color: module.active ? 'primary.main' : 'text.secondary',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {module.title.includes('Nutritional') ? (
                        <Tune sx={{ fontSize: 14 }} />
                      ) : module.title.includes('Annotation') ? (
                        <Label sx={{ fontSize: 14 }} />
                      ) : (
                        <Article sx={{ fontSize: 14 }} />
                      )}
                    </Box>

                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          fontWeight: 900,
                          fontSize: 10.8,
                          lineHeight: 1.2,
                        }}
                      >
                        {module.title}
                      </Typography>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: 'block',
                          fontSize: 9.8,
                          lineHeight: 1.25,
                          mt: 0.25,
                        }}
                      >
                        {module.description}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Box>

          {/* Right: active editor */}
          <Box sx={{ p: 1.6 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1.05 }}
            >
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Tune sx={{ fontSize: 16, color: 'primary.main' }} />

                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                  }}
                >
                  Nutritional parameter editor
                </Typography>
              </Stack>

              <CreateButton icon={<Add sx={{ fontSize: 14 }} />} label="Create parameter" />
            </Stack>

            <Paper
              variant="outlined"
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: 'background.paper',
                mb: 1.2,
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1.25fr 0.55fr 0.45fr 0.55fr 0.55fr',
                  columnGap: 1,
                  px: 1.2,
                  py: 0.8,
                  bgcolor: (theme) => alpha(theme.palette.grey[500], 0.04),
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                {['Name', 'Code', 'Unit', 'Label unit', 'Actions'].map((head) => (
                  <Typography
                    key={head}
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 800, fontSize: 10.2 }}
                  >
                    {head}
                  </Typography>
                ))}
              </Box>

              {nutritionalParameters.map((param, index) => (
                <Box
                  key={param.name}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1.25fr 0.55fr 0.45fr 0.55fr 0.55fr',
                    columnGap: 1,
                    alignItems: 'center',
                    px: 1.2,
                    py: 0.86,
                    borderBottom:
                      index !== nutritionalParameters.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 900,
                      fontSize: 10.8,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {param.name}
                  </Typography>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 800, fontSize: 10.6 }}
                  >
                    {param.code}
                  </Typography>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 800, fontSize: 10.6 }}
                  >
                    {param.unit}
                  </Typography>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 800, fontSize: 10.6 }}
                  >
                    {param.labelUnit}
                  </Typography>

                  <Stack direction="row" spacing={0.45}>
                    <ActionButton type="edit" />
                    <ActionButton type="delete" />
                  </Stack>
                </Box>
              ))}
            </Paper>

            {/* Bottom: secondary configurable resources */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 1,
              }}
            >
              <Paper
                variant="outlined"
                sx={{
                  p: 1.15,
                  borderRadius: 2,
                  bgcolor: (theme) => alpha(theme.palette.grey[500], 0.025),
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 0.85 }}
                >
                  <Stack direction="row" spacing={0.65} alignItems="center">
                    <Label sx={{ fontSize: 15, color: 'primary.main' }} />
                    <Typography variant="caption" sx={{ fontWeight: 900 }}>
                      Annotation labels
                    </Typography>
                  </Stack>

                  <Add sx={{ fontSize: 15, color: 'primary.main' }} />
                </Stack>

                <Stack direction="row" spacing={0.45} flexWrap="wrap" useFlexGap>
                  {annotationExamples.map((label) => (
                    <Box
                      key={label}
                      sx={{
                        px: 0.65,
                        py: 0.3,
                        borderRadius: 999,
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                        color: 'primary.main',
                        border: '1px solid',
                        borderColor: (theme) => alpha(theme.palette.primary.main, 0.18),
                        fontSize: 9.7,
                        fontWeight: 800,
                        maxWidth: '100%',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {label}
                    </Box>
                  ))}
                </Stack>
              </Paper>

              <Paper
                variant="outlined"
                sx={{
                  p: 1.15,
                  borderRadius: 2,
                  bgcolor: (theme) => alpha(theme.palette.grey[500], 0.025),
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 0.85 }}
                >
                  <Stack direction="row" spacing={0.65} alignItems="center">
                    <UploadFile sx={{ fontSize: 15, color: 'primary.main' }} />
                    <Typography variant="caption" sx={{ fontWeight: 900 }}>
                      Documents
                    </Typography>
                  </Stack>

                  <Add sx={{ fontSize: 15, color: 'primary.main' }} />
                </Stack>

                <Stack spacing={0.55}>
                  {documentExamples.map((doc) => (
                    <Stack
                      key={doc}
                      direction="row"
                      spacing={0.6}
                      alignItems="center"
                    >
                      <PictureAsPdf sx={{ fontSize: 13, color: 'primary.main' }} />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontWeight: 800,
                          fontSize: 9.8,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {doc}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}